/**
 * 引用源追溯 API — F4（契约见 kickoff 02 §3）。
 *
 * GET  /api/citation-sources                    引用源聚合列表（主域归一 + 分平台透视）
 * GET  /api/citation-sources/{domain}/articles  文章级下钻（子域仅在此层出现）
 * POST /api/citation-sources/mark-owned         标记/取消自有（轻量写 RR2，幂等）
 *
 * 后端端点并行开发中；调用方必须自行 try/catch 做优雅降级（空态展示，不红屏）。
 */
import client from "./client";
import type {
  CitationSourcesData,
  CitationArticle,
  PaginatedData,
} from "../types";

/** 引用源列表查询参数。 */
export interface CitationSourceParams {
  brand_id: string;
  platform?: string; // 全部时缺省不传；豆包/DeepSeek/通义千问/元宝/文心一言
  tier?: string;
  owned?: "all" | "owned" | "not_owned";
  page?: number;
  size?: number;
}

/** 获取引用源聚合列表（brand_id 必填）。 */
export async function getCitationSources(
  params: CitationSourceParams,
): Promise<CitationSourcesData> {
  return client.get<unknown, CitationSourcesData>("/citation-sources", {
    params,
  });
}

/** 获取指定主域的文章级明细（brand_id 必填，platform 可选）。 */
export async function getCitationSourceArticles(
  domain: string,
  params: { brand_id: string; platform?: string; page?: number; size?: number },
): Promise<PaginatedData<CitationArticle>> {
  return client.get<unknown, PaginatedData<CitationArticle>>(
    `/citation-sources/${encodeURIComponent(domain)}/articles`,
    { params },
  );
}

/** 标记/取消自有引用源（幂等：重复标记返回当前状态）。 */
export async function markCitationSourceOwned(body: {
  domain: string;
  brand_id: string;
  owned: boolean;
}): Promise<{ domain: string; owned: boolean }> {
  return client.post<unknown, { domain: string; owned: boolean }>(
    "/citation-sources/mark-owned",
    body,
  );
}

/** 评估媒体匹配：标记该品牌哪些溯源域名无对应可投媒体（仅写状态，不入投放库）。 */
export async function assessMediaMatch(
  brandId: string,
): Promise<{
  brand_id: string;
  domains_total: number;
  domains_matched: number;
  no_match_domains: string[];
  taxonomy: string[];
}> {
  return client.post<unknown, {
    brand_id: string;
    domains_total: number;
    domains_matched: number;
    no_match_domains: string[];
    taxonomy: string[];
  }>("/citation-sources/assess-media-match", { brand_id: brandId });
}
